import { computed, unref, type ComputedRef, type Ref } from "vue";

type MaybeReactive<T> = T | Ref<T> | ComputedRef<T>;

type PersistedFetchOpts = {
  key?: MaybeReactive<string>;
  watch?: any[];
  server?: boolean;
  headers?: MaybeReactive<Record<string, string>>;
  credentials?: RequestCredentials;
  enabled?: MaybeReactive<boolean>;
};

function createRequestHeaders(
  headers: Record<string, string> = {},
  locale = "cs"
) {
  const normalized = new Headers();

  normalized.set("content-type", "application/json");
  normalized.set("content-language", locale || "cs");

  Object.entries(headers).forEach(([key, value]) => {
    if (value != null) {
      normalized.set(key, value);
    }
  });

  return Object.fromEntries(normalized.entries());
}

function stableStringify(value: any): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  const keys = Object.keys(value).sort();

  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`;
}

function getCurrentLocale() {
  const nuxtApp = useNuxtApp();
  return nuxtApp.$i18n?.locale?.value || "cs";
}

export async function usePersistedFetch<
  TData = any,
  TVars extends Record<string, any> = Record<string, any>
>(
  operationName: string,
  variables: MaybeReactive<TVars> = {} as TVars,
  opts: PersistedFetchOpts = {}
) {
  const runtimeConfig = useRuntimeConfig();

  const apiUrl = runtimeConfig.public.apiUrl;
  const resolvedVariables = computed(() => unref(variables));
  const resolvedHeaders = computed(() => unref(opts.headers || {}));
  const isEnabled = computed(() => unref(opts.enabled ?? true));
  const currentLocale = computed(() => getCurrentLocale());

  const asyncKey = computed(
    () =>
      unref(opts.key) ||
      `pqfetch:${operationName}:${currentLocale.value}:${stableStringify(
        resolvedVariables.value
      )}`
  );

  const { data, pending, error, refresh } = await useAsyncData<TData | null>(
    asyncKey,
    async () => {
      if (!isEnabled.value) {
        return null;
      }

      const res: any = await $fetch(apiUrl, {
        method: "POST",
        headers: createRequestHeaders(
          resolvedHeaders.value,
          currentLocale.value || "cs"
        ),
        body: {
          operationName,
          variables: resolvedVariables.value,
        },
        credentials: opts.credentials ?? "include",
      });

      if (res?.errors?.length) {
        throw createError({
          statusCode: 500,
          statusMessage: res.errors[0]?.message || "API request failed",
          data: res.errors,
        });
      }

      return (res?.data as TData) ?? null;
    },
    {
      watch: [
        currentLocale,
        resolvedVariables,
        resolvedHeaders,
        isEnabled,
        ...(opts.watch || []),
      ],
      server: opts.server ?? true,
    }
  );

  return { data, pending, error, refresh };
}

export async function persistedRequest<
  TData = any,
  TVars extends Record<string, any> = Record<string, any>
>(
  operationName: string,
  variables: TVars = {} as TVars,
  opts: {
    headers?: Record<string, string>;
    credentials?: RequestCredentials;
  } = {}
) {
  const runtimeConfig = useRuntimeConfig();

  const apiUrl = runtimeConfig.public.apiUrl;
  const currentLocale = getCurrentLocale();

  const res: any = await $fetch(apiUrl, {
    method: "POST",
    headers: createRequestHeaders(opts.headers || {}, currentLocale || "cs"),
    body: {
      operationName,
      variables,
    },
    credentials: opts.credentials ?? "include",
  });

  if (res?.errors?.length) {
    throw createError({
      statusCode: 500,
      statusMessage: res.errors[0]?.message || "API request failed",
      data: res.errors,
    });
  }

  return (res?.data as TData) ?? null;
}